import { MapPin, Phone } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { PageHero, CTABand } from "@/components/Layout";
import { business, serviceAreas } from "@/data/site";
import { track } from "@/lib/analytics";

const ServiceAreas = () => {
  useSeo({
    title: `Service Areas — Plumbing, Electrical & HVAC Across New York | ${business.name}`,
    description: "We serve all five NYC boroughs — Manhattan, Brooklyn, Queens, The Bronx & Staten Island — plus surrounding neighborhoods. Find your area and request service.",
  });

  const boroughs = ["Manhattan", "Brooklyn", "Queens", "The Bronx", "Staten Island"];

  return (
    <>
      <PageHero eyebrow="Where We Work" title="Serving All of New York" subtitle="From Manhattan high-rises to Staten Island homes, our licensed team is local, close by, and ready to help." />

      <section className="max-w-7xl mx-auto px-4 py-16 sm:py-24" data-testid="areas-boroughs">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#059669] mb-3">All Five Boroughs</p>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight mb-4">Your Local New York Contractor</h2>
          <p className="text-[#475569] text-lg">We're proud to serve homeowners and property owners across the entire city.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-16">
          {boroughs.map((b) => (
            <div key={b} className="bg-[#0F2E4C] text-white p-6 text-center hover:bg-[#059669] transition-colors" data-testid={`borough-${b.toLowerCase().replace(/\s/g, "-")}`}>
              <MapPin className="w-7 h-7 mx-auto mb-3 text-[#34D399]" />
              <div className="font-display font-bold text-lg">{b}</div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <h3 className="font-display font-bold text-2xl mb-5">Neighborhoods We Cover</h3>
            <div className="flex flex-wrap gap-2">
              {serviceAreas.map((a) => (
                <span key={a} className="border border-[#E2E8F0] bg-white px-4 py-2 text-sm font-medium">{a}</span>
              ))}
            </div>
            <p className="text-[#475569] mt-6">Don't see your neighborhood? <a href={business.phoneHref} className="text-[#059669] font-bold hover:underline">Call us</a> — we likely cover it too.</p>
          </div>
          <div className="bg-white border border-[#E2E8F0] p-6" data-testid="areas-map">
            <div className="aspect-[4/3] bg-[#0F2E4C] flex flex-col items-center justify-center text-center text-white p-6">
              <MapPin className="w-10 h-10 text-[#34D399] mb-3" />
              <p className="text-slate-300">Interactive coverage map / Google Maps embed goes here.</p>
              <p className="text-xs text-slate-500 mt-2">Add the client's real map embed before launch.</p>
            </div>
            <a href={business.phoneHref} onClick={() => track("call_now_click", { location: "service-areas" })} className="mt-4 flex items-center justify-center gap-2 bg-[#059669] text-white font-bold uppercase tracking-wide py-3 hover:bg-[#047857] transition-colors" data-testid="areas-call-btn"><Phone className="w-5 h-5" /> {business.phoneDisplay}</a>
          </div>
        </div>
      </section>

      <CTABand heading="Serving Your New York Neighborhood" />
    </>
  );
};

export default ServiceAreas;
